// ============================================================================
// Building time — where a night manager's punched hours went (item #997).
//
// Pure arithmetic for the AM's "Building time" panel. The minutes of each
// visit are worked out ONCE, in the database view `v_building_visits` (start no
// earlier than the night; end at the earliest of its own check-out, the next
// check-in and the night's end). This file only adds them up.
//
// ⛔ THE TOTAL IS THE NIGHT'S PAID MINUTES, NEVER THE SUM OF VISITS. Driving /
// between buildings is what is left: punched minus visits. So the panel's rows
// always add up to the punched hours to the minute — by construction, not by
// rounding — and nothing here can make a night longer or shorter than it was
// paid. Visits change none of the hours; they only say where they went.
// ============================================================================

export interface BuildingTimeNight {
  segment_id: string;
  shift_date: string;
  started_at: string | null;
  ended_at: string | null;
  paid_minutes: number;
  segment_status: string;
}

export interface BuildingTimeVisit {
  visit_id: string;
  segment_id: string;
  facility_id: string;
  place_display: string;
  in_at: string;
  out_at: string;
  minutes: number;
}

export interface BuildingShare {
  facility_id: string;
  place_display: string;
  visits: number;
  minutes: number;
  /** Of the punched minutes, 0–1. */
  share: number;
}

export type Stretch =
  | { kind: 'building'; facility_id: string; place_display: string; from: string; to: string; minutes: number }
  | { kind: 'between'; from: string; to: string; minutes: number };

export interface NightTimeline {
  segment_id: string;
  shift_date: string;
  punched_minutes: number;
  visit_minutes: number;
  between_minutes: number;
  stretches: Stretch[];
}

export interface BuildingTimeSummary {
  punched_minutes: number;
  visit_minutes: number;
  between_minutes: number;
  between_share: number;
  buildings: BuildingShare[];
  nights: NightTimeline[];
}

const minutesBetween = (a: string, b: string): number =>
  Math.max(0, Math.floor((Date.parse(b) - Date.parse(a)) / 60000));

/** Nights in [from, through] (calendar dates, inclusive), voided nights left out. */
export function summarizeBuildingTime(
  nights: BuildingTimeNight[],
  visits: BuildingTimeVisit[],
  from: string,
  through: string,
): BuildingTimeSummary {
  const inRange = nights.filter((n) => n.segment_status !== 'void' && n.shift_date >= from && n.shift_date <= through);
  const ids = new Set(inRange.map((n) => n.segment_id));
  const mine = visits.filter((v) => ids.has(v.segment_id));

  const byBuilding = new Map<string, BuildingShare>();
  for (const v of mine) {
    const b = byBuilding.get(v.facility_id) ?? { facility_id: v.facility_id, place_display: v.place_display, visits: 0, minutes: 0, share: 0 };
    b.visits += 1;
    b.minutes += v.minutes;
    byBuilding.set(v.facility_id, b);
  }

  const punched = inRange.reduce((t, n) => t + (n.paid_minutes ?? 0), 0);
  const visitMinutes = mine.reduce((t, v) => t + v.minutes, 0);
  const between = punched - visitMinutes;
  const share = (m: number) => (punched > 0 ? m / punched : 0);

  const buildings = Array.from(byBuilding.values())
    .map((b) => ({ ...b, share: share(b.minutes) }))
    .sort((a, b) => b.minutes - a.minutes || a.place_display.localeCompare(b.place_display));

  const timelines: NightTimeline[] = inRange
    .slice()
    .sort((a, b) => (b.started_at ?? '').localeCompare(a.started_at ?? ''))
    .map((n) => {
      const vs = mine.filter((v) => v.segment_id === n.segment_id).sort((a, b) => a.in_at.localeCompare(b.in_at));
      const stretches: Stretch[] = [];
      let cursor = n.started_at;
      for (const v of vs) {
        if (cursor && Date.parse(v.in_at) > Date.parse(cursor)) {
          stretches.push({ kind: 'between', from: cursor, to: v.in_at, minutes: minutesBetween(cursor, v.in_at) });
        }
        stretches.push({ kind: 'building', facility_id: v.facility_id, place_display: v.place_display, from: v.in_at, to: v.out_at, minutes: v.minutes });
        cursor = v.out_at;
      }
      if (cursor && n.ended_at && Date.parse(n.ended_at) > Date.parse(cursor)) {
        stretches.push({ kind: 'between', from: cursor, to: n.ended_at, minutes: minutesBetween(cursor, n.ended_at) });
      }
      const vm = vs.reduce((t, v) => t + v.minutes, 0);
      return {
        segment_id: n.segment_id,
        shift_date: n.shift_date,
        punched_minutes: n.paid_minutes ?? 0,
        visit_minutes: vm,
        between_minutes: (n.paid_minutes ?? 0) - vm,
        stretches,
      };
    });

  return {
    punched_minutes: punched,
    visit_minutes: visitMinutes,
    between_minutes: between,
    between_share: share(between),
    buildings,
    nights: timelines,
  };
}

/** "5 h 07 m" — minutes in words an AM reads at a glance. */
export function fmtMinutes(m: number): string {
  const sign = m < 0 ? '−' : '';
  const a = Math.abs(m);
  return `${sign}${Math.floor(a / 60)} h ${String(a % 60).padStart(2, '0')} m`;
}
